import React from "react";
import { Link } from "react-router-dom";
import Container from "../components/Container";
import { FaLock, FaEye, FaClock, FaKey, FaServer, FaFileAlt, FaFingerprint, FaGlobe } from "react-icons/fa";
import { HiLightningBolt, HiShieldCheck } from "react-icons/hi";
import { BsShieldLockFill } from "react-icons/bs";

const FeaturesPage = () => {
    return (
        <Container>
            {/* Hero Section */}
            <section className="w-full max-w-6xl mx-auto py-12 px-4">
                <div className="text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Safe Whisper <span className="text-primaryColor">Features</span>
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
                        Discover how Safe Whisper combines HTTPS, AES-256-GCM encryption at rest, and one-time links so sensitive text does not linger in email or chat logs.
                    </p>
                    <Link
                        to="/"
                        className="bg-primaryColorHover hover:bg-sky-800 rounded-full py-3 px-8 text-white font-medium text-lg w-96 transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-lg"
                    >
                        <FaKey className="text-lg" />
                        Create Encrypted Message
                    </Link>
                </div>
            </section>

            {/* Key Features Section */}
            <section className="w-full max-w-6xl mx-auto py-12 px-4 bg-slate-800 bg-opacity-50 rounded-xl mb-16">
                <h2 className="text-3xl font-bold text-white text-center mb-12">
                    Why Choose Safe Whisper?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div className="bg-slate-700 rounded-lg p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                        <div className="bg-primaryColor text-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <FaLock size={20} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">AES-256-GCM at rest</h3>
                        <p className="text-gray-300 mb-4">
                            Messages are sent over TLS, then encrypted on the server with AES-256-GCM using a unique salt and IV per message. Ciphertext is stored until the link is read once or revoked.
                        </p>
                    </div>

                    <div className="bg-slate-700 rounded-lg p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                        <div className="bg-primaryColor text-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <FaEye size={20} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">Self-Destructing Messages</h3>
                        <p className="text-gray-300 mb-4">
                            Once read, messages are permanently deleted from our servers, leaving no trace of your sensitive information.
                        </p>
                    </div>

                    <div className="bg-slate-700 rounded-lg p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                        <div className="bg-primaryColor text-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <FaClock size={20} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">One-time access</h3>
                        <p className="text-gray-300 mb-4">
                            Each secret link works once: after a successful read the ciphertext is deleted. Senders can also destroy a link before it is opened.
                        </p>
                    </div>

                    <div className="bg-slate-700 rounded-lg p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                        <div className="bg-primaryColor text-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <FaServer size={20} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">Minimal footprint</h3>
                        <p className="text-gray-300 mb-4">
                            No accounts are required. Messages exist as encrypted blobs until someone uses the secret URL; plaintext exists briefly on the server during encrypt and decrypt over HTTPS.
                        </p>
                    </div>

                    <div className="bg-slate-700 rounded-lg p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                        <div className="bg-primaryColor text-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <FaFileAlt size={20} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">No Sign-up Required</h3>
                        <p className="text-gray-300 mb-4">
                            Use our service instantly without creating an account or sharing any personal information.
                        </p>
                    </div>

                    <div className="bg-slate-700 rounded-lg p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                        <div className="bg-primaryColor text-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <HiLightningBolt size={20} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">Simple and Fast</h3>
                        <p className="text-gray-300 mb-4">
                            Create and share secure messages in seconds with our intuitive, user-friendly interface.
                        </p>
                    </div>
                </div>

                <div className="text-center mt-10">
                    <Link
                        to="/"
                        className="bg-white hover:bg-gray-100 text-slate-800 rounded-lg py-3 px-6 font-medium inline-flex items-center gap-2 transition-colors"
                    >
                        Try It Now
                    </Link>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="w-full max-w-6xl mx-auto py-12 px-4 mb-16">
                <h2 className="text-3xl font-bold text-white text-center mb-12">
                    How It Works
                </h2>

                <div className="relative">
                    {/* Vertical line connector for timeline (hidden on mobile) */}
                    <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-primaryColor transform -translate-x-1/2"></div>

                    <div className="space-y-16">
                        {/* Step 1 */}
                        <div className="flex flex-col md:flex-row items-center">
                            <div className="md:w-1/2 md:pr-12 text-center md:text-right mb-6 md:mb-0">
                                <h3 className="text-2xl font-bold text-white mb-3">Create Your Message</h3>
                                <p className="text-gray-300">
                                    Type your sensitive text in the browser. It is protected in transit with HTTPS to our API.
                                </p>
                            </div>
                            <div className="md:w-16 md:flex md:justify-center md:items-center relative z-10">
                                <div className="bg-primaryColorHover text-white rounded-full w-12 h-12 flex items-center justify-center border-4 border-slate-800">
                                    1
                                </div>
                            </div>
                            <div className="md:w-1/2 md:pl-12 mt-6 md:mt-0">
                                <div className="bg-slate-700 rounded-lg p-4 shadow-md">
                                    <div className="border border-slate-600 bg-slate-800 p-3 rounded text-white text-sm">
                                        <span className="text-primaryColor">// Your confidential message</span><br/>
                                        Type your sensitive information here...
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex flex-col md:flex-row items-center">
                            <div className="md:w-1/2 md:pr-12 text-center md:text-right order-1 md:order-1 mb-6 md:mb-0">
                                <div className="bg-slate-700 rounded-lg p-4 shadow-md">
                                    <div className="bg-sky-900 p-3 rounded text-white text-sm flex items-center justify-center">
                                        <FaLock className="mr-2" /> Encrypting with AES-256-GCM...
                                    </div>
                                </div>
                            </div>
                            <div className="md:w-16 md:flex md:justify-center md:items-center relative z-10 order-2 md:order-2">
                                <div className="bg-primaryColorHover text-white rounded-full w-12 h-12 flex items-center justify-center border-4 border-slate-800">
                                    2
                                </div>
                            </div>
                            <div className="md:w-1/2 md:pl-12 order-3 md:order-3 mt-6 md:mt-0">
                                <h3 className="text-2xl font-bold text-white mb-3">Secure Encryption</h3>
                                <p className="text-gray-300">
                                    The API derives a key with scrypt, encrypts with AES-256-GCM, and stores only ciphertext plus metadata until the one-time read.
                                </p>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex flex-col md:flex-row items-center">
                            <div className="md:w-1/2 md:pr-12 text-center md:text-right mb-6 md:mb-0">
                                <h3 className="text-2xl font-bold text-white mb-3">Share Secure Link</h3>
                                <p className="text-gray-300">
                                    We generate a secure, one-time link that you can share with your recipient through any channel.
                                </p>
                            </div>
                            <div className="md:w-16 md:flex md:justify-center md:items-center relative z-10">
                                <div className="bg-primaryColorHover text-white rounded-full w-12 h-12 flex items-center justify-center border-4 border-slate-800">
                                    3
                                </div>
                            </div>
                            <div className="md:w-1/2 md:pl-12 mt-6 md:mt-0">
                                <div className="bg-slate-700 rounded-lg p-4 shadow-md">
                                    <div className="border border-slate-600 bg-slate-800 p-3 rounded text-white text-sm break-all">
                                        https://safewhisper.com/x7Kp9mN2vQw4RtYbZc1DfGh
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 4 */}
                        <div className="flex flex-col md:flex-row items-center">
                            <div className="md:w-1/2 md:pr-12 text-center md:text-right order-1 md:order-1 mb-6 md:mb-0">
                                <div className="bg-slate-700 rounded-lg p-4 shadow-md">
                                    <div className="bg-red-800 p-3 rounded text-white text-sm flex items-center justify-center">
                                        <FaEye className="mr-2" /> Message self-destructed!
                                    </div>
                                </div>
                            </div>
                            <div className="md:w-16 md:flex md:justify-center md:items-center relative z-10 order-2 md:order-2">
                                <div className="bg-primaryColorHover text-white rounded-full w-12 h-12 flex items-center justify-center border-4 border-slate-800">
                                    4
                                </div>
                            </div>
                            <div className="md:w-1/2 md:pl-12 order-3 md:order-3 mt-6 md:mt-0">
                                <h3 className="text-2xl font-bold text-white mb-3">Self-Destruction</h3>
                                <p className="text-gray-300">
                                    After the recipient reads your message, it's permanently deleted from our servers, leaving no trace.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="text-center mt-16">
                    <Link
                        to="/"
                        className="bg-primaryColorHover hover:bg-sky-800 rounded-full py-3 px-8 text-white font-medium text-lg w-96 transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-lg"
                    >
                        <FaKey className="text-lg" />
                        Create Secure Message
                    </Link>
                </div>
            </section>

            {/* Use Cases Section */}
            <section className="w-full max-w-6xl mx-auto py-12 px-4 bg-slate-800 bg-opacity-50 rounded-xl mb-16">
                <h2 className="text-3xl font-bold text-white text-center mb-12">
                    Perfect For Sharing
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-slate-700 to-slate-600 rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300">
                        <div className="flex items-start gap-4">
                            <div className="bg-sky-100 text-sky-700 p-3 rounded-full">
                                <FaKey size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white mb-2">Passwords</h3>
                                <p className="text-gray-300">
                                    Share login credentials securely without leaving traces in emails or messages.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-slate-700 to-slate-600 rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300">
                        <div className="flex items-start gap-4">
                            <div className="bg-sky-100 text-sky-700 p-3 rounded-full">
                                <FaFingerprint size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white mb-2">Credit Card Details</h3>
                                <p className="text-gray-300">
                                    Send payment information that disappears after being viewed.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-slate-700 to-slate-600 rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300">
                        <div className="flex items-start gap-4">
                            <div className="bg-sky-100 text-sky-700 p-3 rounded-full">
                                <FaFileAlt size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white mb-2">Private Notes</h3>
                                <p className="text-gray-300">
                                    Share confidential information that can't fall into the wrong hands.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-slate-700 to-slate-600 rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300">
                        <div className="flex items-start gap-4">
                            <div className="bg-sky-100 text-sky-700 p-3 rounded-full">
                                <BsShieldLockFill size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white mb-2">2FA Recovery Codes</h3>
                                <p className="text-gray-300">
                                    Share two-factor authentication recovery codes securely.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-slate-700 to-slate-600 rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300">
                        <div className="flex items-start gap-4">
                            <div className="bg-sky-100 text-sky-700 p-3 rounded-full">
                                <FaServer size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white mb-2">API Credentials</h3>
                                <p className="text-gray-300">
                                    Share sensitive development keys and tokens securely.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-slate-700 to-slate-600 rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300">
                        <div className="flex items-start gap-4">
                            <div className="bg-sky-100 text-sky-700 p-3 rounded-full">
                                <FaGlobe size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white mb-2">Cross-Platform</h3>
                                <p className="text-gray-300">
                                    Works on any device with a web browser, no app installation needed.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="text-center mt-10">
                    <Link
                        to="/"
                        className="bg-white hover:bg-gray-100 text-slate-800 rounded-lg py-3 px-6 font-medium inline-flex items-center gap-2 transition-colors"
                    >
                        Start Sharing Securely
                    </Link>
                </div>
            </section>

            {/* Security Features Section */}
            <section className="w-full max-w-6xl mx-auto py-12 px-4 mb-16">
                <h2 className="text-3xl font-bold text-white text-center mb-12">
                    Advanced Security Features
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-slate-700 rounded-lg overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-primaryColor text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">
                                    <HiShieldCheck size={20} />
                                </div>
                                <h3 className="text-xl font-bold text-white">Delete after read</h3>
                            </div>
                            <p className="text-gray-300">
                                After a successful read, the message document is removed from the database. Hosting or CDN logs may still exist outside the app—do not rely on this service for legal hold or compliance archives.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-700 rounded-lg overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-primaryColor text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">
                                    <FaKey size={20} />
                                </div>
                                <h3 className="text-xl font-bold text-white">High-entropy secret links</h3>
                            </div>
                            <p className="text-gray-300">
                                Each link includes an unguessable id. Anyone with the full URL can open the note once while it exists; keep links off public channels you do not trust.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-700 rounded-lg overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-primaryColor text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">
                                    <FaClock size={20} />
                                </div>
                                <h3 className="text-xl font-bold text-white">Post-read countdown</h3>
                            </div>
                            <p className="text-gray-300">
                                After viewing, the UI shows a short countdown before returning you home. There is no separate server-side time-to-live today—unread notes stay until read or manually deleted.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-700 rounded-lg overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-primaryColor text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">
                                    <FaLock size={20} />
                                </div>
                                <h3 className="text-xl font-bold text-white">Manual Destruction</h3>
                            </div>
                            <p className="text-gray-300">
                                Changed your mind? You can manually destroy a message before it's been read, revoking access immediately and permanently.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="w-full max-w-5xl mx-auto mb-16">
                <div className="bg-gradient-to-r from-primaryColor to-primaryColorHover rounded-xl shadow-xl overflow-hidden">
                    <div className="p-8 md:p-12">
                        <div className="flex flex-col md:flex-row items-center justify-between">
                            <div className="md:w-2/3 mb-8 md:mb-0">
                                <h2 className="text-3xl font-bold text-white mb-4">
                                    Ready to secure your communications?
                                </h2>
                                <p className="text-white text-opacity-90 text-lg">
                                    Start sending encrypted self-destructing messages for free, no sign-up required.
                                </p>
                            </div>
                            <div>
                                <Link
                                    to="/"
                                    className="bg-white hover:bg-gray-100 text-sky-700 font-bold py-3 px-8 rounded-lg shadow-lg inline-block transition-colors"
                                >
                                    Create Message Now
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="w-full max-w-4xl mx-auto py-12 px-4 mb-16">
                <h2 className="text-3xl font-bold text-white text-center mb-12">
                    Frequently Asked Questions
                </h2>

                <div className="space-y-6">
                    <div className="bg-slate-700 rounded-lg overflow-hidden">
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-white mb-3">How secure is the encryption?</h3>
                            <p className="text-gray-300">
                                We use AES-256-GCM with per-message salt and IV. Encryption and decryption run on the server over HTTPS; the master secret lives in secure environment configuration, not in the URL.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-700 rounded-lg overflow-hidden">
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-white mb-3">Can you recover a message after it's been viewed?</h3>
                            <p className="text-gray-300">
                                No. Once a message has been viewed, it is permanently deleted from our servers. This is by design - we have no way to recover messages after they've been accessed, ensuring your sensitive information doesn't persist.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-700 rounded-lg overflow-hidden">
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-white mb-3">Do I need to create an account?</h3>
                            <p className="text-gray-300">
                                No. Safe Whisper is designed to be used without accounts or registration. This enhances privacy by eliminating the need to provide any personal information.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-700 rounded-lg overflow-hidden">
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-white mb-3">Is Safe Whisper free to use?</h3>
                            <p className="text-gray-300">
                                Yes, all core features of Safe Whisper are completely free to use. We believe everyone should have access to secure communication tools without cost barriers.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="text-center mt-10">
                    <Link
                        to="/"
                        className="bg-primaryColorHover hover:bg-sky-800 rounded-full py-3 px-8 text-white font-medium text-lg w-96 transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-lg"
                    >
                        <FaKey className="text-lg" />
                        Create Encrypted Message
                    </Link>
                </div>
            </section>

            {/* Trust Indicators */}
            <section className="w-full max-w-5xl mx-auto py-8 px-4 border-t border-slate-700 mb-8">
                <div className="text-center">
                    <p className="text-gray-400 mb-6">HTTPS plus authenticated encryption for ephemeral notes</p>
                    <div className="flex flex-wrap justify-center gap-6">
                        <div className="flex items-center bg-slate-700 px-4 py-2 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primaryColor mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <span className="text-white">AES-256-GCM</span>
                        </div>
                        <div className="flex items-center bg-slate-700 px-4 py-2 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primaryColor mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <span className="text-white">HTTPS Secure</span>
                        </div>
                        <div className="flex items-center bg-slate-700 px-4 py-2 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primaryColor mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <span className="text-white">One-time read</span>
                        </div>
                    </div>
                </div>
            </section>
        </Container>
    );
};

export default FeaturesPage;